import { useState, useEffect } from 'react';
import { 
  CircleStar, 
  Trash2, 
  Search, 
  Filter,
  User,
  Calendar,
  MessageSquare,
  AlertCircle,
  Star,
  Eye
} from 'lucide-react';
import { collection, collectionGroup, query, getDocs, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { usePermissions } from '../hooks/usePermissions';
import Select from '../components/ui/Select';

// Simple toast notification function
const toast = {
  success: (message) => alert(message),
  error: (message) => alert(message)
};

/**
 * Ratings Management Page
 * View and manage all ratings from customers and drivers
 */

export default function RatingsPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_ratings');
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, customer, driver
  const [filterRating, setFilterRating] = useState('all'); // all, 1-5
  const [selectedRating, setSelectedRating] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [cachedPages, setCachedPages] = useState({});

  useEffect(() => {
    fetchAllRatings();
  }, []);

  const fetchAllRatings = async () => {
    try {
      setLoading(true);

      // 1️⃣  One collection-group read pulls EVERY rating across all users
      //     in a single round-trip instead of N (one per user).
      const ratingsSnap = await getDocs(collectionGroup(db, 'ratings'));

      // 2️⃣  Collect the unique user IDs + order IDs we need (rated user via
      //     the parent of the doc path, rater via the document field, and
      //     the order id so we can display its human-readable orderNumber).
      const userIds = new Set();
      const orderIds = new Set();
      const rows = ratingsSnap.docs.map((d) => {
        const data = d.data();
        // path = users/{userId}/ratings/{ratingId}
        const userId = d.ref.parent.parent?.id;
        if (userId) userIds.add(userId);
        if (data.ratedBy) userIds.add(data.ratedBy);
        if (data.orderId) orderIds.add(data.orderId);
        return { id: d.id, userId, ...data };
      });

      // 3️⃣  Fetch every needed user doc + order doc in parallel.
      const userMap = {};
      const orderMap = {};
      await Promise.all([
        ...[...userIds].map(async (uid) => {
          try {
            const s = await getDoc(doc(db, 'users', uid));
            if (s.exists()) userMap[uid] = s.data();
          } catch (e) {
            console.error(`user ${uid} fetch failed`, e);
          }
        }),
        ...[...orderIds].map(async (oid) => {
          try {
            const s = await getDoc(doc(db, 'orders', oid));
            if (s.exists()) orderMap[oid] = s.data();
          } catch (e) {
            console.error(`order ${oid} fetch failed`, e);
          }
        }),
      ]);

      // 4️⃣  Build the final rating rows with names/phones/orderNumber resolved.
      const allRatings = rows.map((r) => {
        const u = (r.userId && userMap[r.userId]) || {};
        const by = (r.ratedBy && userMap[r.ratedBy]) || {};
        const order = (r.orderId && orderMap[r.orderId]) || {};
        return {
          ...r,
          userName: u.name,
          userType: u.type,
          userPhone: u.phoneNumber,
          userEmail: u.email,
          ratedByName: by.name || 'Unknown',
          ratedByType: by.type || 'unknown',
          ratedByPhone: by.phoneNumber || '',
          ratedByEmail: by.email || '',
          orderNumber: order.orderNumber ?? null,
        };
      });

      // Sort newest first.
      allRatings.sort(
        (a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0),
      );

      setRatings(allRatings);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRating = async (rating) => {
    if (!window.confirm(`Are you sure you want to delete this rating?`)) {
      return;
    }

    try {
      const ratingRef = doc(db, 'users', rating.userId, 'ratings', rating.id);
      await deleteDoc(ratingRef);
      
      setRatings(ratings.filter(r => r.id !== rating.id));
      toast.success('Rating deleted successfully');
    } catch (error) {
      console.error('Error deleting rating:', error);
      toast.error('Failed to delete rating');
    }
  };

  const handleViewUser = async (userId, isRatedBy = false) => {
    try {
      // Check cache first
      const cacheKey = `user_${userId}`;
      if (cachedPages[cacheKey]) {
        setSelectedUser({
          id: userId,
          ...cachedPages[cacheKey],
          isRatedBy
        });
        setShowUserModal(true);
        return;
      }

      // Fetch from Firestore if not cached
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        
        // Cache the user data
        setCachedPages(prev => ({ ...prev, [cacheKey]: userData }));
        
        setSelectedUser({
          id: userId,
          ...userData,
          isRatedBy
        });
        setShowUserModal(true);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Failed to load user information');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type) => {
    return type === 'customer' ? 'Customer' : type === 'driver' ? 'Driver' : 'Unknown';
  };

  const getTypeBadgeColor = (type) => {
    return type === 'customer' 
      ? 'bg-blue-100 text-blue-700' 
      : type === 'driver' 
      ? 'bg-green-100 text-green-700' 
      : 'bg-gray-100 text-gray-700';
  };

  const getRatingColor = (value) => {
    if (value >= 4) return 'text-green-600';
    if (value >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Filter ratings
  const filteredRatings = ratings.filter(rating => {
    const matchesSearch = 
      rating.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rating.ratedByName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rating.comment?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = 
      filterType === 'all' || 
      rating.userType === filterType;

    const matchesRating = 
      filterRating === 'all' || 
      rating.value === parseInt(filterRating);

    return matchesSearch && matchesType && matchesRating;
  });

  // Statistics
  const stats = {
    total: ratings.length,
    customers: ratings.filter(r => r.userType === 'customer').length,
    drivers: ratings.filter(r => r.userType === 'driver').length,
    avgRating: ratings.length > 0 
      ? (ratings.reduce((sum, r) => sum + r.value, 0) / ratings.length).toFixed(1)
      : 0
  };

  // Pagination
  const totalPages = Math.ceil(filteredRatings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRatings = filteredRatings.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, filterRating]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading ratings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ratings Management</h1>
          <p className="text-slate-600 mt-1">View and manage all user ratings</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
              <CircleStar className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Ratings</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Customer Ratings</p>
              <p className="text-2xl font-bold text-slate-900">{stats.customers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Driver Ratings</p>
              <p className="text-2xl font-bold text-slate-900">{stats.drivers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Average Rating</p>
              <p className="text-2xl font-bold text-slate-900">{stats.avgRating}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name or comment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>

          {/* Filter by Type */}
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            leftIcon={Filter}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'customer', label: 'Customers' },
              { value: 'driver', label: ' Drivers' }
            ]}
          />

          {/* Filter by Rating */}
          <Select
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            leftIcon={Star}
            options={[
              { value: 'all', label: 'All Ratings' },
              { value: '5', label: '⭐⭐⭐⭐⭐ 5 Stars' },
              { value: '4', label: '⭐⭐⭐⭐ 4 Stars' },
              { value: '3', label: '⭐⭐⭐ 3 Stars' },
              { value: '2', label: '⭐⭐ 2 Stars' },
              { value: '1', label: '⭐ 1 Star' }
            ]}
          />
        </div>
      </div>

      {/* Ratings List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filteredRatings.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No ratings found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-slate-200">
              {paginatedRatings.map((rating) => (
                <div key={`${rating.userId}-${rating.id}`} className="p-6 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      {/* Header */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(rating.userType)}`}>
                          {getTypeLabel(rating.userType)}
                        </span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < rating.value 
                                  ? 'fill-yellow-400 text-yellow-400' 
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                          <span className={`ml-2 font-semibold ${getRatingColor(rating.value)}`}>
                            {rating.value}/5
                          </span>
                        </div>
                      </div>

                      {/* Users Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Rated User */}
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">Rated User</p>
                          <button
                            onClick={() => handleViewUser(rating.userId)}
                            className="flex items-center gap-2 text-slate-900 hover:text-primary-600 transition-colors"
                          >
                            <User className="w-4 h-4" />
                            <span className="font-medium">{rating.userName}</span>
                            <Eye className="w-3 h-3" />
                          </button>
                          <p className="text-sm text-slate-600">{rating.userPhone}</p>
                        </div>

                        {/* Rated By */}
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500">Rated By</p>
                          <button
                            onClick={() => handleViewUser(rating.ratedBy, true)}
                            className="flex items-center gap-2 text-slate-900 hover:text-primary-600 transition-colors"
                          >
                            <User className="w-4 h-4" />
                            <span className="font-medium">{rating.ratedByName}</span>
                            <Eye className="w-3 h-3" />
                          </button>
                          <p className="text-sm text-slate-600">{rating.ratedByPhone}</p>
                        </div>
                      </div>

                      {/* Comment */}
                      {rating.comment && (
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <p className="text-sm text-slate-700">{rating.comment}</p>
                          </div>
                        </div>
                      )}

                      {/* Footer Info */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(rating.createdAt)}</span>
                        </div>
                        {rating.orderNumber != null && (
                          <div>
                            Order:{' '}
                            <span className="font-semibold">
                              #{rating.orderNumber}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {canManage && (
                      <button
                        onClick={() => handleDeleteRating(rating)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete rating"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="border-t border-slate-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredRatings.length)} of {filteredRatings.length} ratings
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === page
                                  ? 'bg-primary-600 text-white'
                                  : 'text-slate-700 hover:bg-slate-50 border border-slate-200'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-2 text-slate-400">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">
                {selectedUser.isRatedBy ? 'Rated By' : 'Rated User'}
              </h3>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setSelectedUser(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Name</p>
                <p className="font-medium text-slate-900">{selectedUser.name}</p>
              </div>

              <div>
                <p className="text-xs text-slate-500">Type</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getTypeBadgeColor(selectedUser.type)}`}>
                  {getTypeLabel(selectedUser.type)}
                </span>
              </div>

              <div>
                <p className="text-xs text-slate-500">Phone Number</p>
                <p className="font-medium text-slate-900">{selectedUser.phoneNumber}</p>
              </div>

              {selectedUser.email && (
                <div>
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{selectedUser.email}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-500">Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                  selectedUser.status === 'banned' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {selectedUser.status || 'active'}
                </span>
              </div>

              <div>
                <p className="text-xs text-slate-500">Created At</p>
                <p className="text-sm text-slate-700">{formatDate(selectedUser.createdAt)}</p>
              </div>

              {selectedUser.bannedAt && (
                <div>
                  <p className="text-xs text-slate-500">Banned At</p>
                  <p className="text-sm text-red-600">{formatDate(selectedUser.bannedAt)}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-500">User ID</p>
                <p className="text-xs font-mono text-slate-600 break-all">{selectedUser.uid}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}